import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Check, Loader2, XCircle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await api.get(
          `/api/checkout/status/${sessionId}`
        );
        
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data to get updated enrolled courses
          await checkAuth();
        } else if (response.data.status === 'expired') {
          setStatus('error');
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount, checkAuth]);

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            COSSY NETWORK
          </span>
        </Link>

        <div className="bg-[#141417] border border-[#27272A] rounded-md p-8 text-center" data-testid="payment-result">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#007AFF] mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Processing Payment
              </h1>
              <p className="text-[#71717A]">Please wait while we verify your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-[#00FF66]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#00FF66]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Payment Successful!
              </h1>
              <p className="text-[#71717A] mb-6">
                Thank you for your purchase. You now have access to your course.
              </p>
              
              {paymentData && (
                <div className="bg-[#09090B] border border-[#27272A] rounded-md p-4 mb-6 text-left">
                  <div className="flex items-center justify-between py-2 border-b border-[#27272A]">
                    <span className="text-[#71717A]">Amount Paid</span>
                    <span className="text-white font-mono">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-[#71717A]">Status</span>
                    <span className="text-[#00FF66]">Completed</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Link to="/dashboard/courses">
                  <Button className="btn-primary w-full" data-testid="go-to-courses-btn">
                    Go to My Courses
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full border-[#27272A] text-[#A1A1AA]">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-[#FF3333]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-[#FF3333]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Payment Issue
              </h1>
              <p className="text-[#71717A] mb-6">
                We couldn't verify your payment. If you believe this is an error, please contact support.
              </p>

              <div className="space-y-3">
                <Link to="/#courses">
                  <Button className="btn-primary w-full">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full border-[#27272A] text-[#A1A1AA]">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
